import { useMarket } from "@app/_context";
import { getCheckoutDelivery } from "@server/requests/checkout";
import { setDeliveryOption } from "@server/requests/delivery/setDeliveryOption";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getCartCookie } from "@/utils/cart/getCartCookie";

import { useCheckoutSummary, useCheckoutZipCode } from "../Checkout.hooks";
import { type DeliveryOption, transformDeliveryOption } from "./Delivery.utils";

/**
 * Get nShift delivery options
 * This will be called when the user enters and submits an address with a zip code
 */
export function useCheckoutDelivery() {
  const { serviceLocale, locale } = useMarket().state.market;
  const zipCode = useCheckoutZipCode();
  const { cartTotal } = useCheckoutSummary();

  return useQuery({
    queryKey: ["checkoutDelivery", zipCode, cartTotal],
    queryFn: () =>
      getCheckoutDelivery({
        locale: serviceLocale,
        cartId: getCartCookie()?.value ?? "",
        zipCode: zipCode ?? "",
      }),
    select: (data) => {
      if (!data?.options?.length) return { ...data, options: [] };
      const options = data.options
        .map((opt) => transformDeliveryOption(opt, locale))
        .filter((opt): opt is DeliveryOption => Boolean(opt));
      return { ...data, options };
    },
    enabled: Boolean(zipCode),
  });
}

/**
 * Set delivery option
 * This will be called when delivery option changes in the nShift widget
 */
export function useSetCheckoutDelivery(
  callback?: (...args: any) => Promise<any>,
) {
  return useMutation({
    mutationKey: ["deliveryOption"],
    mutationFn: setDeliveryOption,
    onSettled: callback,
  });
}
